import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  reconnectEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  MessageSquare,
  GitBranch,
  ArrowLeft,
  Sparkles,
  X,
  Check,
  Variable,
  AlertTriangle,
  Save,
  Loader2,
  Lock,
  Unlock,
  Grid3X3,
} from "lucide-react";
import { getLayoutedElements } from "../utils/flowLayout";
import {
  getFlowById,
  createFlow,
  updateFlow,
  generateFlowWithAI,
  resetFlowSession,
} from "../apis/flowAPI";
import TriggerNode from "../components/FlowNodes/TriggerNode";
import TemplateNode from "../components/FlowNodes/TemplateNode";
import KnowledgeBaseNode from "../components/FlowNodes/KnowledgeBaseNode";
import AIAskQuestionNode from "../components/FlowNodes/AIAskQuestionNode";
import CaptureTextNode from "../components/FlowNodes/CaptureTextNode";
import AIIntentClassifierNode from "../components/FlowNodes/AIIntentClassifierNode";
import AIEntityExtractorNode from "../components/FlowNodes/AIEntityExtractorNode";
import AIResponseGeneratorNode from "../components/FlowNodes/AIResponseGeneratorNode";
import AIKnowledgeBaseAnswerNode from "../components/FlowNodes/AIKnowledgeBaseAnswerNode";
import TextMessageNode from "../components/FlowNodes/TextMessageNode";
import SendMediaNode from "../components/FlowNodes/SendMediaNode";
import SetVariableNode from "../components/FlowNodes/SetVariableNode";
import CodeMapNode from "../components/FlowNodes/CodeMapNode";
import ConditionNode from "../components/FlowNodes/ConditionNode";
import DelayNode from "../components/FlowNodes/DelayNode";
import AssignAgentNode from "../components/FlowNodes/AssignAgentNode";
import EndNode from "../components/FlowNodes/EndNode";
import WebhookNode from "../components/FlowNodes/WebhookNode";
import InteractiveButtonsNode from "../components/FlowNodes/InteractiveButtonsNode";
import InteractiveListNode from "../components/FlowNodes/InteractiveListNode";
import InteractiveUrlNode from "../components/FlowNodes/InteractiveUrlNode";
import InteractiveCarouselUrlNode from "../components/FlowNodes/InteractiveCarouselUrlNode";
import InteractiveCarouselButtonNode from "../components/FlowNodes/InteractiveCarouselButtonNode";
import PreApprovedTemplateNode from "../components/FlowNodes/PreApprovedTemplateNode";
import TransferToFlowNode from "../components/FlowNodes/TransferToFlowNode";
import ControllerNode from "../components/FlowNodes/ControllerNode";
import VariableManager from "../components/FlowBuilder/VariableManager";
import AIChatPanel from "../components/FlowBuilder/AIChatPanel";
import FlowAiHelpMenu from "../components/FlowBuilder/FlowAiHelpSettingsPanel";
import { FlowToolsMenu, FlowTestSessionMenu } from "../components/FlowBuilder/FlowBuilderToolbarMenus";
import {
  applyFlowAiHelpToNodes,
  buildFlowSettingsPayload,
  extractFlowAiHelpFromFlow,
  validateFlowAiHelpSettings,
} from "../utils/flowAiHelpUtils";
import { FlowAddNodeMenu, NodeAddNodePicker } from "../components/FlowBuilder/AddNodePicker";
import { exportFlowJSON } from "../utils/flowTemplateGenerator";
import { validateFlowGraph } from "../utils/flowBuilderValidation";

// Register custom node types
const nodeTypes = {
  trigger: TriggerNode,
  template: TemplateNode,
  knowledgeBase: KnowledgeBaseNode,
  aiAskQuestion: AIAskQuestionNode,
  captureText: CaptureTextNode,
  aiIntentClassifier: AIIntentClassifierNode,
  aiEntityExtractor: AIEntityExtractorNode,
  aiResponseGenerator: AIResponseGeneratorNode,
  aiKnowledgeBaseAnswer: AIKnowledgeBaseAnswerNode,
  textMessage: TextMessageNode,
  sendMedia: SendMediaNode,
  setVariable: SetVariableNode,
  codeMap: CodeMapNode,
  jsonMap: CodeMapNode,
  condition: ConditionNode,
  delay: DelayNode,
  assignAgent: AssignAgentNode,
  end: EndNode,
  webhook: WebhookNode,
  interactiveButtons: InteractiveButtonsNode,
  interactiveList: InteractiveListNode,
  interactiveUrl: InteractiveUrlNode,
  interactiveCarouselUrl: InteractiveCarouselUrlNode,
  interactiveCarouselButton: InteractiveCarouselButtonNode,
  preApprovedTemplate: PreApprovedTemplateNode,
  transferToFlow: TransferToFlowNode,
  controller: ControllerNode,
};

// ── Node type lists (module-level for stable reference) ──
const NODES_NEEDING_VARIABLES = [
  "template", "aiAskQuestion", "captureText", "knowledgeBase",
  "aiIntentClassifier", "aiEntityExtractor", "aiResponseGenerator",
  "aiKnowledgeBaseAnswer", "textMessage", "sendMedia", "setVariable", "codeMap", "jsonMap", "webhook", "trigger",
  "interactiveButtons", "interactiveList", "interactiveUrl",
  "interactiveCarouselUrl", "interactiveCarouselButton", "preApprovedTemplate", "condition",
  "assignAgent", "transferToFlow", "controller",
];

function getDefaultSourceHandle(node) {
  if (!node) return null;
  switch (node.type) {
    case "condition":
      return "true";
    case "interactiveList":
      return "in_list";
    case "controller": {
      const branches = node.data?.branches || [];
      return branches[0]?.id ? `branch_${branches[0].id}` : null;
    }
    case "aiIntentClassifier": {
      const labels = node.data?.labels || [];
      if (labels[0]?.id) return `intent_${labels[0].id}`;
      return "intent_other";
    }
    case "interactiveButtons": {
      const buttons = node.data?.buttons || [];
      return buttons[0]?.id || "default";
    }
    case "preApprovedTemplate": {
      const buttons = (node.data?.buttons || []).filter((b) => b.type === "QUICK_REPLY");
      return buttons[0]?.id || "default";
    }
    case "webhook": {
      const handlers = node.data?.statusHandlers || [];
      return handlers[0]?.id ? `status_${handlers[0].id}` : null;
    }
    default:
      return null;
  }
}

/** Sync KB ↔ AI data refs and add missing attachment edges on load/import. */
function syncKnowledgeBaseAttachments(nodes, edges) {
  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const hasEdge = (source, target) =>
    edges.some((e) => e.source === source && e.target === target);

  const kbConsumerTypes = new Set([
    "aiKnowledgeBaseAnswer",
    "aiResponseGenerator",
    "template",
  ]);

  const syncedNodes = nodes.map((node) => {
    if (
      node.type !== "knowledgeBase" &&
      node.type !== "aiKnowledgeBaseAnswer" &&
      node.type !== "aiResponseGenerator"
    ) {
      return node;
    }

    if (
      (node.type === "aiKnowledgeBaseAnswer" || node.type === "aiResponseGenerator") &&
      node.data?.knowledgeBaseNodeId
    ) {
      const kbId = node.data.knowledgeBaseNodeId;
      const kbNode = nodeById[kbId];
      if (kbNode?.type === "knowledgeBase" && !kbNode.data?.attachedToNodeId) {
        nodeById[kbId] = {
          ...kbNode,
          data: { ...kbNode.data, attachedToNodeId: node.id },
        };
      }
    }

    if (node.type === "knowledgeBase" && node.data?.attachedToNodeId) {
      const targetNode = nodeById[node.data.attachedToNodeId];
      if (
        targetNode &&
        kbConsumerTypes.has(targetNode.type) &&
        !targetNode.data?.knowledgeBaseNodeId
      ) {
        nodeById[targetNode.id] = {
          ...targetNode,
          data: { ...targetNode.data, knowledgeBaseNodeId: node.id },
        };
      }
    }

    return nodeById[node.id] || node;
  });

  const extraEdges = [];
  for (const node of syncedNodes) {
    if (node.type === "knowledgeBase" && node.data?.attachedToNodeId) {
      const targetId = node.data.attachedToNodeId;
      if (!hasEdge(node.id, targetId)) {
        extraEdges.push({
          id: `ekb-${node.id}-${targetId}`,
          source: node.id,
          target: targetId,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: { conditionType: "always", conditionValue: "", buttonId: null },
        });
      }
    }
    if (
      (node.type === "aiKnowledgeBaseAnswer" || node.type === "aiResponseGenerator") &&
      node.data?.knowledgeBaseNodeId
    ) {
      const kbId = node.data.knowledgeBaseNodeId;
      if (!hasEdge(kbId, node.id)) {
        extraEdges.push({
          id: `ekb-${kbId}-${node.id}`,
          source: kbId,
          target: node.id,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: { conditionType: "always", conditionValue: "", buttonId: null },
        });
      }
    }
  }

  return {
    nodes: syncedNodes,
    edges: extraEdges.length ? [...edges, ...extraEdges] : edges,
  };
}

// Default (system) flow variables – always present and not removable
const DEFAULT_FLOW_VARIABLES = [
  { id: "default_phone_number", name: "phone_number", description: "Customer phone number", isSystem: true },
  { id: "default_last_user_message", name: "last_user_message", description: "Last user message", isSystem: true },
  { id: "default_message_id", name: "message_id", description: "WhatsApp message ID of the last inbound message", isSystem: true },
  { id: "default_last_msg_timestamp", name: "last_msg_timestamp", description: "When the last message was sent (UTC)", isSystem: true },
  { id: "default_customer_name", name: "customer_name", description: "Customer display name from WhatsApp profile", isSystem: true },
  { id: "default_keyword", name: "keyword", description: "Keyword (or message) that activated this flow", isSystem: true },
];

const FlowBuilder = () => {
  const { flowId: urlFlowId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChangeRaw] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onNodesChange = useCallback(
    (changes) => onNodesChangeRaw(changes.filter((c) => c.type !== "remove")),
    [onNodesChangeRaw]
  );
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [flowId, setFlowId] = useState(urlFlowId || null);
  const [loading, setLoading] = useState(!!urlFlowId);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [nodeAddPicker, setNodeAddPicker] = useState({ open: false, sourceNodeId: null, x: 0, y: 0 });
  const [aiDescription, setAiDescription] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [showVariablePanel, setShowVariablePanel] = useState(false);
  const [showAiHelpPanel, setShowAiHelpPanel] = useState(false);
  const [allowAiHelpDuringConversation, setAllowAiHelpDuringConversation] = useState(false);
  const [aiHelpKnowledgeBase, setAiHelpKnowledgeBase] = useState("");
  const [aiHelpPanelError, setAiHelpPanelError] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const fileInputRef = useRef(null);
  const edgeReconnectSuccessful = useRef(true);
  /** Last pointer position (screen coords) while over the flow pane — used to place new nodes under the cursor */
  const lastPanePointerScreenRef = useRef({ x: 0, y: 0 });
  /** True after user has moved the mouse over the canvas at least once this session */
  const panePointerSeenRef = useRef(false);
  const flowCanvasContainerRef = useRef(null);

  // ── Hybrid layout state ──
  const [manuallyPlacedNodes, setManuallyPlacedNodes] = useState(new Set());
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [lockManualPositions, setLockManualPositions] = useState(true);

  // ── Test session reset (live WhatsApp testing) ──
  const [testCustomerPhone, setTestCustomerPhone] = useState("");
  const [resetSessionLoading, setResetSessionLoading] = useState(false);
  const [resetSessionMessage, setResetSessionMessage] = useState(null);

  // ── Full-screen node editor (n8n-style) ──
  const [editingNodeId, setEditingNodeId] = useState(null);
  const openNodeEditor = useCallback((nodeId) => setEditingNodeId(nodeId), []);
  const closeNodeEditor = useCallback(() => setEditingNodeId(null), []);

  // ── Centralized Variable Registry (user-added only) ──
  const [flowVariables, setFlowVariables] = useState([]);
  // Array of { id, name, description }

  // Effective list = defaults first, then user variables (no duplicate names with defaults)
  const effectiveFlowVariables = useMemo(
    () => [
      ...DEFAULT_FLOW_VARIABLES,
      ...flowVariables.filter((v) => !DEFAULT_FLOW_VARIABLES.some((d) => d.name === v.name)),
    ],
    [flowVariables]
  );

  const addVariable = useCallback((name, description = "") => {
    const trimmed = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!trimmed) return;
    if (DEFAULT_FLOW_VARIABLES.some((d) => d.name === trimmed)) return; // cannot add same name as default
    setFlowVariables((prev) => {
      if (prev.some((v) => v.name === trimmed)) return prev;
      return [...prev, { id: `var_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`, name: trimmed, description }];
    });
  }, []);

  const removeVariable = useCallback((idOrName) => {
    setFlowVariables((prev) => prev.filter((v) => v.id !== idOrName && v.name !== idOrName));
  }, []);

  // ── Node data change callback ──
  const handleNodeDataChange = useCallback(
    (nodeId, newData) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: newData } : n))
      );
    },
    [setNodes]
  );

  // ── Stable refs for node action callbacks (avoids circular deps) ──
  const deleteNodeRef = useRef(null);
  const duplicateNodeRef = useRef(null);

  // ── Delete node by ID (called from on-node button) ──
  const handleDeleteNodeById = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    },
    [setNodes, setEdges]
  );
  deleteNodeRef.current = handleDeleteNodeById;

  // ── Duplicate node by ID (called from on-node button) ──
  const handleDuplicateNodeById = useCallback(
    (nodeId) => {
      setNodes((nds) => {
        const srcNode = nds.find((n) => n.id === nodeId);
        if (!srcNode) return nds;
        const stripped = {};
        for (const [k, v] of Object.entries(srcNode.data || {})) {
          if (typeof v !== "function") stripped[k] = v;
        }
        const clonedData = JSON.parse(JSON.stringify(stripped));

        // Re-attach all callbacks via refs (stable across renders)
        clonedData.onDeleteNode = (...args) => deleteNodeRef.current?.(...args);
        clonedData.onDuplicateNode = (...args) => duplicateNodeRef.current?.(...args);
        if (NODES_NEEDING_VARIABLES.includes(srcNode.type)) {
          clonedData.onNodeDataChange = handleNodeDataChange;
          clonedData.onCreateVariable = addVariable;
        }

        const newNode = {
          id: `${srcNode.type}-${Date.now()}`,
          type: srcNode.type,
          position: { x: (srcNode.position?.x || 0) + 60, y: (srcNode.position?.y || 0) + 60 },
          data: clonedData,
        };
        return [...nds, newNode];
      });
    },
    [setNodes, handleNodeDataChange, addVariable]
  );
  duplicateNodeRef.current = handleDuplicateNodeById;

  const handleOpenAddNodeMenu = useCallback((nodeId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setNodeAddPicker({
      open: true,
      sourceNodeId: nodeId,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  }, []);

  // ── Propagate callbacks + variables to all nodes ──
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        const extra = {
          onDeleteNode: handleDeleteNodeById,
          onDuplicateNode: handleDuplicateNodeById,
          onOpenAddNodeMenu: handleOpenAddNodeMenu,
          canAddNext: n.type !== "end",
        };
        if (NODES_NEEDING_VARIABLES.includes(n.type)) {
          extra.flowVariables = effectiveFlowVariables;
          extra.onCreateVariable = addVariable;
        }
        if (flowId) {
          extra.flowId = flowId;
        }
        return { ...n, data: { ...n.data, ...extra } };
      })
    );
  }, [effectiveFlowVariables, addVariable, handleDeleteNodeById, handleDuplicateNodeById, handleOpenAddNodeMenu, setNodes, flowId]);

  // ── Connection handler (supports button, intent, and status handles) ──
  const onConnect = useCallback(
    (params) => {
      let conditionType = "always";
      let conditionValue = "";
      let buttonId = null;

      if (params.sourceHandle) {
        if (params.sourceHandle.startsWith("btn_") || params.sourceHandle.startsWith("cbtn_")) {
          buttonId = params.sourceHandle;
          conditionType = "button_click";
          conditionValue = buttonId;

          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === params.source) {
                const updatedButtons = (n.data.buttons || []).map((btn) =>
                  btn.id === buttonId
                    ? { ...btn, connectedNodeId: params.target }
                    : btn
                );
                return { ...n, data: { ...n.data, buttons: updatedButtons } };
              }
              return n;
            })
          );
        } else if (params.sourceHandle === "intent_other") {
          // Intent Classifier "Other" fallback handle
          conditionType = "intent_other";
          conditionValue = "__intent_other__";
        } else if (params.sourceHandle.startsWith("intent_")) {
          // Intent Classifier or Smart Assistant intent handle
          const labelId = params.sourceHandle.replace("intent_", "");
          conditionType = "intent";

          setNodes((nds) =>
            nds.map((n) => {
              if (n.id !== params.source) return n;
              if (n.type === "aiIntentClassifier") {
                const updatedLabels = (n.data.labels || []).map((lbl) =>
                  lbl.id === labelId
                    ? { ...lbl, connectedNodeId: params.target }
                    : lbl
                );
                const matchedLabel = updatedLabels.find((l) => l.id === labelId);
                conditionValue = matchedLabel?.value || "";
                return { ...n, data: { ...n.data, labels: updatedLabels } };
              }
              if (n.type === "aiKnowledgeBaseAnswer") {
                const updatedIntents = (n.data.intents || []).map((intent) =>
                  intent.id === labelId
                    ? { ...intent, connectedNodeId: params.target }
                    : intent
                );
                const matchedIntent = updatedIntents.find((i) => i.id === labelId);
                conditionValue = matchedIntent?.value || "";
                return { ...n, data: { ...n.data, intents: updatedIntents } };
              }
              return n;
            })
          );
        } else if (params.sourceHandle === "answer" || params.sourceHandle === "no_answer") {
          conditionType = params.sourceHandle;
          conditionValue = params.sourceHandle;
        } else if (params.sourceHandle.startsWith("status_")) {
          // Webhook status handler handle — handle id is "status_<sh.id>"
          const handlerId = params.sourceHandle.replace("status_", "");
          conditionType = "status";
          conditionValue = params.sourceHandle; // full handle id for persistence

          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === params.source) {
                const updatedHandlers = (n.data.statusHandlers || []).map((sh) =>
                  sh.id === handlerId
                    ? { ...sh, connectedNodeId: params.target }
                    : sh
                );
                return { ...n, data: { ...n.data, statusHandlers: updatedHandlers } };
              }
              return n;
            })
          );
        } else if (params.sourceHandle === "true") {
          conditionType = "true";
        } else if (params.sourceHandle === "false") {
          conditionType = "false";
        } else if (params.sourceHandle === "other") {
          // Template "Other" handle — user reply didn't match any quick-reply button
          conditionType = "other";
          conditionValue = "__other__";
        } else if (params.sourceHandle === "entity_found" || params.sourceHandle === "entity_not_found") {
          // AI Entity Extractor result handles
          conditionType = "entity_result";
          conditionValue = params.sourceHandle;
        } else if (params.sourceHandle === "in_list" || params.sourceHandle === "not_in_list") {
          conditionType = "list_reply";
          conditionValue = params.sourceHandle;
        } else if (params.sourceHandle?.startsWith("branch_") || params.sourceHandle === "default") {
          conditionType = "controller";
          conditionValue = params.sourceHandle;
        }
      }

      // Handle KnowledgeBase → TemplateNode or KnowledgeBase → AIKnowledgeBaseAnswer attachment
      setNodes((nds) => {
        const sourceNode = nds.find((n) => n.id === params.source);
        const targetNode = nds.find((n) => n.id === params.target);

        if (
          sourceNode?.type === "knowledgeBase" &&
          (targetNode?.type === "template" ||
            targetNode?.type === "aiKnowledgeBaseAnswer" ||
            targetNode?.type === "aiResponseGenerator")
        ) {
          return nds.map((n) => {
            if (n.id === params.source) {
              return { ...n, data: { ...n.data, attachedToNodeId: params.target } };
            }
            if (n.id === params.target) {
              return { ...n, data: { ...n.data, knowledgeBaseNodeId: params.source } };
            }
            return n;
          });
        }
        return nds;
      });

      const newEdge = {
        ...params,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: {
          conditionType,
          conditionValue,
          buttonId,
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onNodeClick = useCallback(() => {}, []);

  // ── Load existing flow ──
  useEffect(() => {
    const loadFlow = async () => {
      if (!urlFlowId) return;

      try {
        setLoading(true);
        const response = await getFlowById(urlFlowId);
        const flow = response.data.flow;

        setFlowName(flow.name || "Untitled Flow");
        setFlowDescription(flow.description || "");
        setFlowId(flow.id);

        // Load variables if saved (exclude names that match defaults so we don't duplicate)
        if (flow.variables) {
          setFlowVariables(
            flow.variables
              .filter((v) => !DEFAULT_FLOW_VARIABLES.some((d) => d.name === v.name))
              .map((v, i) => ({
                id: `var_loaded_${i}`,
                name: v.name,
                description: v.description || "",
              }))
          );
        }

        const reactFlowNodes = flow.nodes.map((node) => {
          // Migrate sendMessage nodes to template nodes
          const baseCallbacks = {
            onDeleteNode: handleDeleteNodeById,
            onDuplicateNode: handleDuplicateNodeById,
          };

          if (node.node_type === "sendMessage") {
            return {
              id: node.node_id,
              type: "template",
              position: { x: node.position_x, y: node.position_y },
              data: {
                ...getDefaultNodeData("template"),
                body: { text: node.config?.message || "", variables: [] },
                onNodeDataChange: handleNodeDataChange,
                ...baseCallbacks,
              },
            };
          }

          // Migrate legacy askQuestion → captureText (raw text, no AI)
          if (node.node_type === "askQuestion") {
            return {
              id: node.node_id,
              type: "captureText",
              position: { x: node.position_x, y: node.position_y },
              data: {
                ...getDefaultNodeData("captureText"),
                question: node.config?.question || "",
                variableName: node.config?.variableName || "selected",
                emptyAnswerMessage: node.config?.emptyAnswerMessage || "",
                onNodeDataChange: handleNodeDataChange,
                flowVariables: [],
                onCreateVariable: addVariable,
                ...baseCallbacks,
              },
            };
          }
         
          let nodeData = { ...getDefaultNodeData(node.node_type), ...(node.config || {}) };
          if ((node.node_type === "interactiveCarouselUrl" || node.node_type === "interactiveCarouselButton") && !nodeData.body) nodeData = { ...nodeData, body: { text: "" } };
          if (node.node_type === "condition" && !nodeData.logicBetween) {
            const conds = nodeData.conditions || [];
            nodeData = { ...nodeData, logicBetween: Array(Math.max(0, conds.length - 1)).fill("and") };
          }
          const needsCallback = NODES_NEEDING_VARIABLES.includes(node.node_type);
          return {
            id: node.node_id,
            type: node.node_type,
            position: { x: node.position_x, y: node.position_y },
            data: {
              ...nodeData,
              ...baseCallbacks,
              ...(needsCallback
                ? { onNodeDataChange: handleNodeDataChange, flowVariables: [], onCreateVariable: addVariable }
                : {}),
            },
          };
        });

        // Build a lookup of node configs by node_id for handle restoration
        const nodeConfigMap = {};
        for (const node of flow.nodes) {
          nodeConfigMap[node.node_id] = node.config || {};
        }

        const reactFlowEdges = flow.connections.map((conn) => {
          const edge = {
            id: `e${conn.source_node_id}-${conn.target_node_id}-${conn.condition_value || "default"}`,
            source: conn.source_node_id,
            target: conn.target_node_id,
            markerEnd: { type: MarkerType.ArrowClosed },
            data: {
              conditionType: conn.condition_type || "always",
              conditionValue: conn.condition_value || "",
              buttonId: conn.button_id || null,
            },
          };

          if (
            conn.condition_type === "true" ||
            conn.condition_type === "false"
          ) {
            edge.sourceHandle = conn.condition_type;
          }

          // Restore button handle from button_id column (legacy) or condition_value
          if (conn.button_id) {
            edge.sourceHandle = conn.button_id;
            edge.data.buttonId = conn.button_id;
          } else if (conn.condition_type === "button_click" && conn.condition_value && !edge.sourceHandle) {
            edge.sourceHandle = conn.condition_value; // condition_value stores the buttonId
            edge.data.buttonId = conn.condition_value;
          }

          // Restore intent classifier / smart assistant handle: sourceHandle = "intent_<id>"
          if (conn.condition_type === "intent" && conn.condition_value && !edge.sourceHandle) {
            const sourceConfig = nodeConfigMap[conn.source_node_id] || {};
            const labels = sourceConfig.labels || [];
            const intents = sourceConfig.intents || [];
            const matchedLabel = labels.find(
              (l) => l.value && l.value.toLowerCase() === conn.condition_value.toLowerCase()
            );
            const matchedIntent = intents.find(
              (i) => i.value && i.value.toLowerCase() === conn.condition_value.toLowerCase()
            );
            if (matchedLabel) {
              edge.sourceHandle = `intent_${matchedLabel.id}`;
            } else if (matchedIntent) {
              edge.sourceHandle = `intent_${matchedIntent.id}`;
            }
          }

          // Restore KB session exit handles
          if (
            (conn.condition_type === "answer" || conn.condition_type === "no_answer") &&
            !edge.sourceHandle
          ) {
            edge.sourceHandle = conn.condition_type;
          }

          // Restore intent "Other" fallback handle
          if (conn.condition_type === "intent_other" && !edge.sourceHandle) {
            edge.sourceHandle = "intent_other";
          }

          // Restore status handle from condition_value (e.g. "status_<sh.id>")
          if (conn.condition_type === "status" && conn.condition_value && !edge.sourceHandle) {
            edge.sourceHandle = conn.condition_value;
          }

          // Restore "Other" handle for template node
          if (conn.condition_type === "other" && !edge.sourceHandle) {
            edge.sourceHandle = "other";
          }

          // Restore entity_found / entity_not_found handles
          if (conn.condition_type === "entity_result" && conn.condition_value && !edge.sourceHandle) {
            edge.sourceHandle = conn.condition_value; // "entity_found" or "entity_not_found"
          }

          // Restore interactive list in_list / not_in_list handles
          if (conn.condition_type === "list_reply" && conn.condition_value && !edge.sourceHandle) {
            edge.sourceHandle = conn.condition_value;
          }

          // Restore controller branch handles
          if (conn.condition_type === "controller" && conn.condition_value && !edge.sourceHandle) {
            edge.sourceHandle = conn.condition_value;
          }

          // Restore sourceHandle from saved data (intent_ / other handles)
          if (conn.sourceHandle && !edge.sourceHandle) {
            edge.sourceHandle = conn.sourceHandle;
          }

          return edge;
        });

        // Rebuild connectedNodeId on template buttons and intent labels from edges
        for (const node of reactFlowNodes) {
          const nodeEdges = reactFlowEdges.filter((e) => e.source === node.id);

          if (node.type === "template" && node.data.buttons) {
            node.data.buttons = node.data.buttons.map((btn) => {
              if (btn.type === "QUICK_REPLY") {
                const btnEdge = nodeEdges.find(
                  (e) => e.data?.buttonId === btn.id || e.sourceHandle === btn.id
                );
                return btnEdge
                  ? { ...btn, connectedNodeId: btnEdge.target }
                  : btn;
              }
              return btn;
            });
          }

          if (node.type === "interactiveButtons" && node.data.buttons) {
            node.data.buttons = node.data.buttons.map((btn) => {
              const btnEdge = nodeEdges.find(
                (e) => e.data?.buttonId === btn.id || e.sourceHandle === btn.id
              );
              return btnEdge ? { ...btn, connectedNodeId: btnEdge.target } : btn;
            });
          }

          if (node.type === "preApprovedTemplate" && node.data.buttons) {
            node.data.buttons = node.data.buttons.map((btn) => {
              const btnEdge = nodeEdges.find(
                (e) => e.data?.buttonId === btn.id || e.sourceHandle === btn.id
              );
              return btnEdge ? { ...btn, connectedNodeId: btnEdge.target } : btn;
            });
          }

          if (node.type === "aiIntentClassifier" && node.data.labels) {
            node.data.labels = node.data.labels.map((lbl) => {
              const lblEdge = nodeEdges.find(
                (e) => e.sourceHandle === `intent_${lbl.id}`
              );
              return lblEdge
                ? { ...lbl, connectedNodeId: lblEdge.target }
                : lbl;
            });
          }

          if (node.type === "aiKnowledgeBaseAnswer" && node.data.intents?.length) {
            node.data.intents = node.data.intents.map((intent) => {
              const intentEdge = nodeEdges.find(
                (e) => e.sourceHandle === `intent_${intent.id}`
              );
              return intentEdge
                ? { ...intent, connectedNodeId: intentEdge.target }
                : intent;
            });
          }

          if (node.type === "webhook" && node.data.statusHandlers) {
            node.data.statusHandlers = node.data.statusHandlers.map((sh) => {
              const shEdge = nodeEdges.find(
                (e) => e.sourceHandle === `status_${sh.id}`
              );
              return shEdge
                ? { ...sh, connectedNodeId: shEdge.target }
                : sh;
            });
          }
        }

        // Use saved positions as-is; use Format / Format All to auto-layout
        const aiHelpSettings = extractFlowAiHelpFromFlow(flow, flow.nodes);
        setAllowAiHelpDuringConversation(aiHelpSettings.allowAiHelpDuringConversation);
        setAiHelpKnowledgeBase(aiHelpSettings.aiHelpKnowledgeBase);

        const kbSynced = syncKnowledgeBaseAttachments(
          applyFlowAiHelpToNodes(reactFlowNodes, aiHelpSettings),
          reactFlowEdges
        );
        setNodes(kbSynced.nodes);
        setEdges(kbSynced.edges);
      } catch (error) {
        console.error("Error loading flow:", error);
        alert("Failed to load flow");
        navigate("/flow-list");
      } finally {
        setLoading(false);
      }
    };

    loadFlow();
  }, [urlFlowId, navigate, setNodes, setEdges, handleNodeDataChange, addVariable]);

  const handleResetTestSession = async () => {
    if (!flowId || !testCustomerPhone.trim()) return;

    try {
      setResetSessionLoading(true);
      setResetSessionMessage(null);
      const response = await resetFlowSession(flowId, {
        customerPhone: testCustomerPhone.trim(),
      });
      const data = response.data;
      setResetSessionMessage({
        type: data.reset ? "success" : "info",
        text: data.message || (data.reset ? "Session reset — send trigger keyword again" : "No active session found"),
      });
      if (data.traceId) {
        console.info("[FlowSessionReset]", data.traceId, data.steps || data.cleared);
      }
    } catch (error) {
      const errorText = error.response?.data?.error || "Failed to reset session";
      setResetSessionMessage({ type: "error", text: errorText });
    } finally {
      setResetSessionLoading(false);
    }
  };

  const onPaneMouseMove = useCallback((event) => {
    lastPanePointerScreenRef.current = { x: event.clientX, y: event.clientY };
    panePointerSeenRef.current = true;
  }, []);

  /** Flow coordinates for a newly added node: cursor on canvas, else viewport center, with small offset to center on pointer */
  const getFlowPositionForNewNode = useCallback(() => {
    const HALF_W = 150;
    const HALF_H = 90;
    const rf = reactFlowInstance;
    const toPos = (screenX, screenY) => {
      const p = rf.screenToFlowPosition({ x: screenX, y: screenY });
      let x = p.x - HALF_W;
      let y = p.y - HALF_H;
      if (snapToGrid) {
        const g = 20;
        x = Math.round(x / g) * g;
        y = Math.round(y / g) * g;
      }
      return { x, y };
    };

    if (rf && panePointerSeenRef.current) {
      const { x, y } = lastPanePointerScreenRef.current;
      return toPos(x, y);
    }
    if (rf && flowCanvasContainerRef.current) {
      const rect = flowCanvasContainerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return toPos(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
    }
    return { x: 80, y: 80 };
  }, [reactFlowInstance, snapToGrid]);

  // ── Add node ──
  const createNodeObject = useCallback(
    (type, position) => {
      const defaultData = getDefaultNodeData(type);
      const needsCallback = NODES_NEEDING_VARIABLES.includes(type);
      const nodeData = {
        ...defaultData,
        onDeleteNode: handleDeleteNodeById,
        onDuplicateNode: handleDuplicateNodeById,
        onOpenAddNodeMenu: handleOpenAddNodeMenu,
        canAddNext: type !== "end",
        ...(needsCallback
          ? {
              onNodeDataChange: handleNodeDataChange,
              flowVariables: effectiveFlowVariables,
              onCreateVariable: addVariable,
            }
          : {}),
      };

      return {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: nodeData,
      };
    },
    [
      handleDeleteNodeById,
      handleDuplicateNodeById,
      handleOpenAddNodeMenu,
      handleNodeDataChange,
      effectiveFlowVariables,
      addVariable,
    ]
  );

  const addNode = (type) => {
    const newNode = createNodeObject(type, getFlowPositionForNewNode());
    setNodes((nds) => [...nds, newNode]);
  };

  const addConnectedNode = useCallback(
    (sourceNodeId, type) => {
      const sourceNode = nodes.find((n) => n.id === sourceNodeId);
      if (!sourceNode) return;

      let x = (sourceNode.position?.x || 0) + 380;
      let y = sourceNode.position?.y || 0;
      if (snapToGrid) {
        const g = 20;
        x = Math.round(x / g) * g;
        y = Math.round(y / g) * g;
      }

      const newNode = createNodeObject(type, { x, y });
      const sourceHandle = getDefaultSourceHandle(sourceNode);

      setNodes((nds) => [...nds, newNode]);
      setEdges((eds) =>
        addEdge(
          {
            id: `e-${sourceNodeId}-${newNode.id}`,
            source: sourceNodeId,
            target: newNode.id,
            sourceHandle: sourceHandle || undefined,
            type: "smoothstep",
            animated: false,
            markerEnd: { type: MarkerType.ArrowClosed, color: "#3b82f6" },
            style: { stroke: "#3b82f6", strokeWidth: 2 },
            data: {
              conditionType: "always",
              conditionValue: "",
              buttonId: sourceHandle?.startsWith("btn_") || sourceHandle?.startsWith("cbtn_") ? sourceHandle : null,
            },
          },
          eds
        )
      );
    },
    [nodes, snapToGrid, createNodeObject, setNodes, setEdges]
  );

  const getDefaultNodeData = (type) => {
    switch (type) {
      case "trigger":
        return {
          label: "Flow Trigger",
          keywords: [],
          triggerType: "keyword",
          triggerOnAnyKeyword: false,
          allowAiHelpDuringConversation: false,
          aiHelpKnowledgeBase: "",
          expectedTransferVariables: [],
          apiExpectedVariables: { required: [], optional: [] },
        };
      case "template":
        return {
          label: "Template Message",
          templateName: "",
          templateLanguage: "en_US",
          header: { format: "NONE", text: "", mediaUrl: "", fileName: "" },
          body: { text: "", variables: [] },
          footer: { text: "" },
          buttons: [],
          knowledgeBaseNodeId: null,
        };
      case "knowledgeBase":
        return {
          label: "Knowledge Base",
          content: "",
          attachments: [],
          attachedToNodeId: null,
        };
      case "aiAskQuestion":
        return {
          label: "AI Ask Question",
          question: "",
          variableName: "",
          aiPrompt: "",
          answerType: "text",
          entities: [],
        };
      case "captureText":
        return {
          label: "Ask question (text)",
          question: "",
          variableName: "selected",
          emptyAnswerMessage: "",
        };
      case "aiIntentClassifier":
        return {
          label: "AI Intent Classifier",
          labels: [],
          fallbackVariable: "",
          prompt: "",
        };
      case "aiEntityExtractor":
        return {
          label: "AI Entity Extractor",
          entities: [],
        };
      case "aiResponseGenerator":
        return {
          label: "AI Response Generator",
          prompt: "",
          systemPrompt: "",
          variableNames: [],
          knowledgeBaseNodeId: null,
          answerMode: "gpt_only",
          storeInVariable: "",
          sendReply: true,
        };
      case "aiKnowledgeBaseAnswer":
        return {
          label: "AI Smart Assistant",
          knowledgeBaseNodeId: null,
          contextVariableNames: [],
          storeInVariable: "",
          sendReply: true,
          sessionMode: true,
          maxQuestions: 10,
          introMessage: "",
          prePromptMessagesCount: 6,
          exitOnNoAnswer: true,
          intents: [],
          intentPrompt: "",
          intentFallbackVariable: "",
        };
      case "textMessage":
        return {
          label: "Text Message",
          message: "",
        };
      case "sendMedia":
        return {
          label: "Send Media",
          mode: "manual",
          items: [
            {
              id: `med_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              type: "image",
              mediaUrl: "",
              caption: "",
              fileName: "",
            },
          ],
          variableName: "",
        };
      case "setVariable":
        return {
          label: "Set Variable",
          items: [
            {
              id: `set_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              variableName: "",
              source: "manual",
              value: "",
              sourceVariable: "",
            },
          ],
        };
      case "codeMap":
      case "jsonMap":
        return {
          label: "Code Transform",
          inputVariable: "",
          outputVariables: [{ id: `out_${Date.now()}`, variableName: "" }],
          code: "",
        };
      case "condition":
        return { label: "Condition", conditions: [], logicBetween: [] };
      case "delay":
        return { label: "Delay", duration: 5, unit: "seconds" };
      case "assignAgent":
        return { label: "Assign Agent", agentIds: [], agentNames: [], allocationMethod: "roundRobin" };
      case "webhook":
        return {
          label: "Webhook",
          webhookUrl: "",
          method: "POST",
          headers: "{}",
          requestBody: "",
          inputVariable: "",
          responseVariable: "webhook_response",
          responseMessage: "Data fetched successfully",
          responseMapping: [],
          statusHandlers: [],
        };
      case "interactiveButtons":
        return {
          label: "Interactive Buttons",
          header: { format: "NONE", text: "", mediaUrl: "", fileName: "" },
          body: { text: "", variables: [] },
          footer: { text: "" },
          buttons: [],
          selectionVariable: "",
        };
      case "interactiveList":
        return {
          label: "Interactive List",
          header: { format: "NONE", text: "", mediaUrl: "", fileName: "" },
          body: { text: "", variables: [] },
          footer: { text: "" },
          listLabel: "Select an option",
          listSections: [{ title: "", rows: [{ title: "", description: "" }] }],
          listFromVariable: "",
          listVariableFieldMap: { title: "", description: "" },
          selectionVariable: "",
        };
      case "interactiveUrl":
        return {
          label: "Interactive URL",
          header: { format: "NONE", text: "", mediaUrl: "", fileName: "" },
          body: { text: "", variables: [] },
          footer: { text: "" },
          urlLabel: "Visit",
          url: "",
        };
      case "interactiveCarouselUrl":
        return {
          label: "Carousel URL",
          body: { text: "" },
          cardsSource: "manual",
          cards: [],
          cardsVariable: "",
          cardFieldMap: { type: "", body: "", mediaUrl: "", urlLabel: "", url: "" },
          selectionVariable: "",
        };
      case "interactiveCarouselButton":
        return {
          label: "Carousel Button",
          body: { text: "" },
          cardsSource: "manual",
          cards: [],
          cardsVariable: "",
          cardFieldMap: { type: "", body: "", mediaUrl: "", buttonLabel: "", buttonPayload: "" },
          selectionVariable: "",
          labelVariable: "",
        };
      case "preApprovedTemplate":
        return {
          label: "Pre-approved Template",
          phoneId: null,
          templateId: null,
          templateName: "",
          templateLanguage: "en_US",
          parameterFormat: "numbered",
          templateSnapshot: null,
          variableMappings: [],
          buttons: [],
          headerMediaFormat: "NONE",
          headerMediaOverride: "",
          selectionVariable: "",
        };
      case "end":
        return { label: "End Flow" };
      case "transferToFlow":
        return {
          label: "Transfer to Flow",
          targetFlowId: null,
          targetFlowName: "",
          transferVariableMode: "all",
          transferVariables: [],
        };
      case "controller":
        return {
          label: "Controller",
          branches: [
            { id: `branch_${Date.now()}_1`, label: "", variable: "", operator: "equals", value: "", dataType: "auto" },
          ],
          hasDefault: true,
        };
      default:
        return { label: type };
    }
  };

  // ── Track manual drag → mark node as manually placed ──
  const onNodeDragStop = useCallback((_event, node) => {
    setManuallyPlacedNodes((prev) => {
      const next = new Set(prev);
      next.add(node.id);
      return next;
    });
  }, []);

  // ── Format flow: full re-layout (respects lockManualPositions) ──
  const handleFormatFlow = useCallback(() => {
    const skipIds = lockManualPositions ? manuallyPlacedNodes : new Set();
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes, edges, "LR", { skipNodeIds: skipIds }
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2 }), 50);
  }, [nodes, edges, setNodes, setEdges, reactFlowInstance, lockManualPositions, manuallyPlacedNodes]);

  // ── Format ALL (ignores manual positions, resets tracking) ──
  const handleFormatAll = useCallback(() => {
    setManuallyPlacedNodes(new Set());
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes, edges, "LR", { skipNodeIds: new Set() }
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2 }), 50);
  }, [nodes, edges, setNodes, setEdges, reactFlowInstance]);

  // ── Clear whiteboard ──
  const handleClearCanvas = useCallback(() => {
    if (!window.confirm("Clear the entire flow? This cannot be undone.")) return;
    setNodes([]);
    setEdges([]);
  }, [setNodes, setEdges]);

  // ── Edge deletion: sync source-node data when edges are removed ──
  const handleEdgesDelete = useCallback(
    (deletedEdges) => {
      setNodes((nds) =>
        nds.map((node) => {
          let updated = false;
          let data = node.data;

          for (const edge of deletedEdges) {
            if (edge.source !== node.id) continue;
            const handle = edge.sourceHandle || edge.data?.buttonId;
            if (!handle) continue;

            if (
              (node.type === "template" || node.type === "interactiveButtons" || node.type === "preApprovedTemplate") &&
              data.buttons?.length
            ) {
              const newButtons = data.buttons.map((btn) =>
                btn.id === handle ? { ...btn, connectedNodeId: null } : btn
              );
              if (newButtons !== data.buttons) {
                data = { ...data, buttons: newButtons };
                updated = true;
              }
            }

            if (node.type === "aiIntentClassifier" && data.labels?.length) {
              if (handle.startsWith("intent_")) {
                const labelId = handle.replace("intent_", "");
                const newLabels = data.labels.map((lbl) =>
                  lbl.id === labelId ? { ...lbl, connectedNodeId: null } : lbl
                );
                data = { ...data, labels: newLabels };
                updated = true;
              }
            }

            if (node.type === "webhook" && data.statusHandlers?.length) {
              if (handle.startsWith("status_")) {
                const handlerId = handle.replace("status_", "");
                const newHandlers = data.statusHandlers.map((sh) =>
                  sh.id === handlerId ? { ...sh, connectedNodeId: null } : sh
                );
                data = { ...data, statusHandlers: newHandlers };
                updated = true;
              }
            }
          }

          return updated ? { ...node, data } : node;
        })
      );
    },
    [setNodes]
  );

  // ── Edge reconnection: drag edge to a new handle or drop on pane to delete ──
  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      edgeReconnectSuccessful.current = true;
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
    },
    [setEdges]
  );

  const onReconnectEnd = useCallback(
    (_, edge) => {
      if (!edgeReconnectSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        handleEdgesDelete([edge]);
      }
      edgeReconnectSuccessful.current = true;
    },
    [setEdges, handleEdgesDelete]
  );

  const flowAiHelpSettings = useMemo(
    () => ({ allowAiHelpDuringConversation, aiHelpKnowledgeBase }),
    [allowAiHelpDuringConversation, aiHelpKnowledgeBase]
  );

  const nodesWithFlowAiHelp = useMemo(
    () => applyFlowAiHelpToNodes(nodes, flowAiHelpSettings),
    [nodes, flowAiHelpSettings]
  );

  const reactFlowNodes = useMemo(
    () =>
      nodesWithFlowAiHelp.map((n) => ({
        ...n,
        data: {
          ...n.data,
          __editingNodeId: editingNodeId,
          openNodeEditor,
          closeNodeEditor,
        },
      })),
    [nodesWithFlowAiHelp, editingNodeId, openNodeEditor, closeNodeEditor]
  );

  const syncFlowAiHelpToNodes = useCallback(
    (settings) => {
      setNodes((nds) => applyFlowAiHelpToNodes(nds, settings));
    },
    [setNodes]
  );

  const handleAllowAiHelpChange = useCallback(
    (enabled) => {
      setAllowAiHelpDuringConversation(enabled);
      setAiHelpPanelError("");
      syncFlowAiHelpToNodes({
        allowAiHelpDuringConversation: enabled,
        aiHelpKnowledgeBase,
      });
    },
    [aiHelpKnowledgeBase, syncFlowAiHelpToNodes]
  );

  const handleAiHelpKnowledgeBaseChange = useCallback(
    (value) => {
      setAiHelpKnowledgeBase(value);
      setAiHelpPanelError("");
      syncFlowAiHelpToNodes({
        allowAiHelpDuringConversation,
        aiHelpKnowledgeBase: value,
      });
    },
    [allowAiHelpDuringConversation, syncFlowAiHelpToNodes]
  );

  // ── Run validation ──
  const runValidation = useCallback(() => {
    const aiHelpError = validateFlowAiHelpSettings(flowAiHelpSettings);
    setAiHelpPanelError(aiHelpError || "");
    if (aiHelpError) setShowAiHelpPanel(true);

    const result = validateFlowGraph(nodesWithFlowAiHelp, edges, effectiveFlowVariables);
    console.log("Validation result:", result);
    setValidationErrors(result.errors);
    setValidationWarnings(result.warnings);
    if (!result.valid || result.warnings.length > 0 || aiHelpError) {
      setShowValidationPanel(true);
    } else {
      setShowValidationPanel(false);
    }
    return { ...result, valid: result.valid && !aiHelpError };
  }, [nodesWithFlowAiHelp, edges, effectiveFlowVariables, flowAiHelpSettings]);

  // ── Export JSON ──
  const handleExportJSON = () => {
    const result = runValidation();
    if (!result.valid) return; // Block export on validation errors
    const flowJSON = exportFlowJSON(flowName, flowDescription, nodesWithFlowAiHelp, edges, effectiveFlowVariables);
    const jsonString = JSON.stringify(flowJSON, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${flowName.replace(/\s+/g, "_") || "flow"}_export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Copy JSON to clipboard ──
  const handleCopyJSON = async () => {
    const result = runValidation();
    if (!result.valid) return; // Block copy on validation errors
    const flowJSON = exportFlowJSON(flowName, flowDescription, nodesWithFlowAiHelp, edges, effectiveFlowVariables);
    const jsonString = JSON.stringify(flowJSON, null, 2);
    try {
      await navigator.clipboard.writeText(jsonString);
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // ── Import flow from JSON file (export format) ──
  const applyImportedFlow = useCallback(
    (flow) => {
      const importedVars = (flow.variables || []).filter(
        (v) => !DEFAULT_FLOW_VARIABLES.some((d) => d.name === (v.name || ""))
      );
      const importedFlowVariables = [
        ...DEFAULT_FLOW_VARIABLES,
        ...importedVars.map((v, i) => ({
          id: `import_${i}_${v.name}`,
          name: v.name,
          description: v.description || "",
        })),
      ];

      setFlowName(flow.name || "Imported Flow");
      setFlowDescription(flow.description || "");
      setFlowVariables(
        importedVars.map((v, i) => ({
          id: `import_${i}_${v.name}`,
          name: v.name,
          description: v.description || "",
        }))
      );
      setFlowId(null);
      setValidationErrors([]);
      setValidationWarnings([]);

      const conns = flow.connections || [];
      const reactFlowNodes = (flow.nodes || []).map((node) => {
        const type = node.type || "template";
        const defaultData = getDefaultNodeData(type);
        let nodeData = { ...defaultData, ...(node.data || {}) };
        if ((type === "interactiveCarouselUrl" || type === "interactiveCarouselButton") && !nodeData.body) {
          nodeData = { ...nodeData, body: { text: "" } };
        }
        if (type === "condition" && !nodeData.logicBetween && (nodeData.conditions || []).length) {
          nodeData = {
            ...nodeData,
            logicBetween: Array(Math.max(0, (nodeData.conditions || []).length - 1)).fill("and"),
          };
        }
        const needsCallback = NODES_NEEDING_VARIABLES.includes(type);
        const data = {
          ...nodeData,
          onDeleteNode: handleDeleteNodeById,
          onDuplicateNode: handleDuplicateNodeById,
          ...(needsCallback
            ? {
                onNodeDataChange: handleNodeDataChange,
                flowVariables: importedFlowVariables,
                onCreateVariable: addVariable,
              }
            : {}),
        };

        return {
          id: node.id,
          type,
          position: {
            x: node.position?.x ?? 0,
            y: node.position?.y ?? 0,
          },
          data,
        };
      });

      const reactFlowEdges = conns.map((c, i) => ({
        id: `e${c.source}-${c.target}-${c.conditionValue || "default"}-${i}`,
        source: c.source,
        target: c.target,
        sourceHandle: c.sourceHandle || undefined,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: {
          conditionType: c.conditionType || "always",
          conditionValue: c.conditionValue || "",
          buttonId: c.buttonId ?? null,
        },
      }));

      // Rebuild connectedNodeId from edges
      for (const node of reactFlowNodes) {
        const nodeEdges = reactFlowEdges.filter((e) => e.source === node.id);
        if (node.type === "template" && node.data.buttons) {
          node.data.buttons = node.data.buttons.map((btn) => {
            if (btn.type === "QUICK_REPLY") {
              const btnEdge = nodeEdges.find((e) => e.data?.buttonId === btn.id || e.sourceHandle === btn.id);
              return btnEdge ? { ...btn, connectedNodeId: btnEdge.target } : btn;
            }
            return btn;
          });
        }
        if (node.type === "interactiveButtons" && node.data.buttons) {
          node.data.buttons = node.data.buttons.map((btn) => {
            const btnEdge = nodeEdges.find((e) => e.data?.buttonId === btn.id || e.sourceHandle === btn.id);
            return btnEdge ? { ...btn, connectedNodeId: btnEdge.target } : btn;
          });
        }
        if (node.type === "aiIntentClassifier" && node.data.labels) {
          node.data.labels = node.data.labels.map((lbl) => {
            const lblEdge = nodeEdges.find((e) => e.sourceHandle === `intent_${lbl.id}`);
            return lblEdge ? { ...lbl, connectedNodeId: lblEdge.target } : lbl;
          });
        }
        if (node.type === "aiKnowledgeBaseAnswer" && node.data.intents?.length) {
          node.data.intents = node.data.intents.map((intent) => {
            const intentEdge = nodeEdges.find((e) => e.sourceHandle === `intent_${intent.id}`);
            return intentEdge ? { ...intent, connectedNodeId: intentEdge.target } : intent;
          });
        }
        if (node.type === "webhook" && node.data.statusHandlers) {
          node.data.statusHandlers = node.data.statusHandlers.map((sh) => {
            const shEdge = nodeEdges.find((e) => e.sourceHandle === `status_${sh.id}`);
            return shEdge ? { ...sh, connectedNodeId: shEdge.target } : sh;
          });
        }
      }

      const kbSynced = syncKnowledgeBaseAttachments(reactFlowNodes, reactFlowEdges);
      setNodes(kbSynced.nodes);
      setEdges(kbSynced.edges);

      const result = validateFlowGraph(kbSynced.nodes, kbSynced.edges, importedFlowVariables);
      setValidationErrors(result.errors);
      setValidationWarnings(result.warnings);
      if (result.errors.length > 0 || result.warnings.length > 0) {
        setShowValidationPanel(true);
      }
    },
    [handleNodeDataChange, addVariable, handleDeleteNodeById, handleDuplicateNodeById, setNodes, setEdges]
  );

  const handleImportFlow = useCallback(
    (file) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result;
          if (typeof text !== "string") {
            alert("Failed to read file.");
            return;
          }
          const parsed = JSON.parse(text);
          const flow = parsed.flow || parsed;
          if (!flow || !Array.isArray(flow.nodes) || !Array.isArray(flow.connections)) {
            alert("Invalid flow file: expected an object with 'flow.name', 'flow.nodes' (array), and 'flow.connections' (array).");
            return;
          }
          applyImportedFlow(flow);
        } catch (err) {
          alert(err instanceof SyntaxError ? "Invalid JSON: " + err.message : "Failed to import flow.");
        }
      };
      reader.readAsText(file, "UTF-8");
    },
    [applyImportedFlow]
  );

  const onFileInputChange = (e) => {
    const file = e.target?.files?.[0];
    if (file) {
      handleImportFlow(file);
      e.target.value = "";
    }
  };

  // ── Save Flow to backend ──
  const handleSaveFlow = async () => {
    if (!flowName.trim()) {
      alert("Please enter a flow name before saving.");
      return;
    }

    const result = runValidation();
    if (!result.valid) return; // Block save on validation errors

    const flowJSON = exportFlowJSON(flowName, flowDescription, nodesWithFlowAiHelp, edges, effectiveFlowVariables);

    // Transform to the shape the backend expects
    const aiHelpPayload = buildFlowSettingsPayload({
      allowAiHelpDuringConversation,
      aiHelpKnowledgeBase,
    });

    const payload = {
      name: flowJSON.flow.name,
      description: flowJSON.flow.description,
      isActive: true,
      variables: flowJSON.flow.variables || [],
      flowSettings: aiHelpPayload,
      nodes: flowJSON.flow.nodes.map((n) => ({
        nodeId: n.id,
        type: n.type,
        positionX: Math.round(n.position?.x || 0),
        positionY: Math.round(n.position?.y || 0),
        config:
          n.type === "trigger"
            ? { ...(n.data || {}), ...aiHelpPayload }
            : n.data || {},
      })),
      connections: flowJSON.flow.connections.map((c) => ({
        sourceNodeId: c.source,
        targetNodeId: c.target,
        conditionType: c.conditionType || "always",
        conditionValue: c.conditionValue || "",
      })),
    };

    try {
      setSaving(true);

      if (flowId) {
        // Update existing flow
        await updateFlow(flowId, payload);
        setLastSavedAt(new Date());
        console.log("Flow updated successfully");
      } else {
        // Create new flow
        const response = await createFlow(payload);
        const newFlowId = response.data.flowId;
        setFlowId(newFlowId);
        setLastSavedAt(new Date());
        // Update URL so refreshing opens this flow
        navigate(`/flows/builder/${newFlowId}`, { replace: true });
        console.log("Flow created successfully, id:", newFlowId);
      }
    } catch (error) {
      console.error("Error saving flow:", error);
      const errorData = error.response?.data;
      if (errorData?.requiresUpgrade || errorData?.requiresSubscription) {
        alert(errorData.message || "Subscription required to save flows.");
      } else {
        alert(errorData?.error || "Failed to save flow. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ── AI generation ──
  const handleGenerateWithAI = async () => {
    if (!aiDescription.trim()) {
      alert("Please enter a description of your flow");
      return;
    }

    try {
      setAiGenerating(true);
      const response = await generateFlowWithAI(
        aiDescription,
        flowName || "AI Generated Flow"
      );

      const generatedFlow = response.data.flow;

      const reactFlowNodes = generatedFlow.nodes.map((node) => {
        let type = node.type;
        if (type === "sendMessage") type = "template";
        if (type === "askQuestion") type = "captureText";

        const defaultData = getDefaultNodeData(type);
        const needsCallback = NODES_NEEDING_VARIABLES.includes(type);
        let nodeData;

        const baseCallbacks = {
          onDeleteNode: handleDeleteNodeById,
          onDuplicateNode: handleDuplicateNodeById,
        };

        if (type === "template") {
          nodeData = {
            ...defaultData,
            ...(node.data || {}),
            body: {
              text: node.data?.message || node.data?.body?.text || "",
              variables: node.data?.body?.variables || [],
            },
            onNodeDataChange: handleNodeDataChange,
            flowVariables: effectiveFlowVariables,
            onCreateVariable: addVariable,
            ...baseCallbacks,
          };
        } else if (type === "aiAskQuestion" || type === "captureText") {
          nodeData = {
            ...defaultData,
            ...(node.data || {}),
            onNodeDataChange: handleNodeDataChange,
            flowVariables: effectiveFlowVariables,
            onCreateVariable: addVariable,
            ...baseCallbacks,
          };
        } else if (needsCallback) {
          nodeData = {
            ...(node.data || defaultData),
            onNodeDataChange: handleNodeDataChange,
            flowVariables: effectiveFlowVariables,
            onCreateVariable: addVariable,
            ...baseCallbacks,
          };
        } else {
          nodeData = { ...(node.data || defaultData), ...baseCallbacks };
        }

        return {
          id: node.id,
          type,
          position: node.position || {
            x: Math.random() * 400,
            y: Math.random() * 400,
          },
          data: nodeData,
        };
      });

      const reactFlowEdges = generatedFlow.connections.map((conn, idx) => {
        const edge = {
          id: `e${conn.source}-${conn.target}-${idx}`,
          source: conn.source,
          target: conn.target,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: {
            conditionType: conn.conditionType || "always",
            conditionValue: conn.conditionValue || "",
            buttonId: conn.buttonId || null,
          },
        };

        if (conn.conditionType === "true" || conn.conditionType === "false") {
          edge.sourceHandle = conn.conditionType;
        }
        if (conn.buttonId) {
          edge.sourceHandle = conn.buttonId;
        }
        // Restore status handle from conditionValue
        if (conn.conditionType === "status" && conn.conditionValue && !edge.sourceHandle) {
          edge.sourceHandle = conn.conditionValue;
        }
        // Restore "Other" handle for template node
        if (conn.conditionType === "other" && !edge.sourceHandle) {
          edge.sourceHandle = "other";
        }
        // Restore intent "Other" fallback handle
        if (conn.conditionType === "intent_other" && !edge.sourceHandle) {
          edge.sourceHandle = "intent_other";
        }
        // Restore entity_found / entity_not_found handles
        if (conn.conditionType === "entity_result" && conn.conditionValue && !edge.sourceHandle) {
          edge.sourceHandle = conn.conditionValue;
        }
        // Restore interactive list in_list / not_in_list handles
        if (conn.conditionType === "list_reply" && conn.conditionValue && !edge.sourceHandle) {
          edge.sourceHandle = conn.conditionValue;
        }
        // Restore controller branch handles
        if (conn.conditionType === "controller" && conn.conditionValue && !edge.sourceHandle) {
          edge.sourceHandle = conn.conditionValue;
        }
        // Restore sourceHandle if present
        if (conn.sourceHandle && !edge.sourceHandle) {
          edge.sourceHandle = conn.sourceHandle;
        }

        return edge;
      });

      setNodes(reactFlowNodes);
      setEdges(reactFlowEdges);

      if (response.data.name && !flowName) {
        setFlowName(response.data.name);
      }

      setShowAIPanel(false);
      setAiDescription("");

      alert("Flow generated successfully! You can now edit and export it.");
    } catch (error) {
      console.error("Error generating flow with AI:", error);
      const errorResponse = error.response?.data;
      if (!errorResponse?.requiresSubscription) {
        alert(
          errorResponse?.error ||
            "Failed to generate flow. Please try again."
        );
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // ── Apply AI-generated flow from chat panel ──
  const handleApplyAIFlow = useCallback(
    (generatedFlow) => {
      if (!generatedFlow?.nodes) return;

      const reactFlowNodes = generatedFlow.nodes.map((node) => {
        let type = node.type;
        if (type === "sendMessage") type = "template";
        if (type === "askQuestion") type = "captureText";

        const defaultData = getDefaultNodeData(type);
        const needsCallback = NODES_NEEDING_VARIABLES.includes(type);
        const baseCallbacks = {
          onDeleteNode: handleDeleteNodeById,
          onDuplicateNode: handleDuplicateNodeById,
        };

        let nodeData;
        if (needsCallback) {
          nodeData = {
            ...defaultData,
            ...(node.data || {}),
            onNodeDataChange: handleNodeDataChange,
            flowVariables: effectiveFlowVariables,
            onCreateVariable: addVariable,
            ...baseCallbacks,
          };
        } else {
          nodeData = { ...defaultData, ...(node.data || {}), ...baseCallbacks };
        }

        return {
          id: node.id,
          type,
          position: node.position || { x: Math.random() * 400, y: Math.random() * 400 },
          data: nodeData,
        };
      });

      const nodeMap = {};
      for (const n of reactFlowNodes) nodeMap[n.id] = n;

      const reactFlowEdges = (generatedFlow.connections || []).map((conn, idx) => {
        const ct = conn.conditionType || "always";
        const cv = conn.conditionValue || "";
        const edge = {
          id: `e${conn.source}-${conn.target}-${idx}`,
          source: conn.source,
          target: conn.target,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: {
            conditionType: ct,
            conditionValue: cv,
            buttonId: conn.buttonId || null,
          },
        };

        if (conn.sourceHandle) {
          edge.sourceHandle = conn.sourceHandle;
        } else if (ct === "true" || ct === "false") {
          edge.sourceHandle = ct;
        } else if (ct === "button_click") {
          edge.sourceHandle = conn.buttonId || cv || null;
        } else if (ct === "other") {
          edge.sourceHandle = "other";
        } else if (ct === "intent") {
          const srcNode = nodeMap[conn.source];
          const labels = srcNode?.data?.labels || [];
          const matched = labels.find(l => l.value && l.value.toLowerCase() === cv.toLowerCase());
          edge.sourceHandle = matched ? `intent_${matched.id}` : cv;
        } else if (ct === "intent_other") {
          edge.sourceHandle = "intent_other";
        } else if (ct === "entity_result") {
          edge.sourceHandle = cv || null;
        } else if (ct === "list_reply") {
          edge.sourceHandle = cv || null;
        } else if (ct === "controller") {
          edge.sourceHandle = cv || null;
        } else if (ct === "status") {
          edge.sourceHandle = cv || null;
        }

        return edge;
      });

      if (Array.isArray(generatedFlow.variables)) {
        for (const v of generatedFlow.variables) {
          if (v.name) addVariable(v.name, v.description || "");
        }
      }

      setNodes(reactFlowNodes);
      setEdges(reactFlowEdges);
      setManuallyPlacedNodes(new Set());
    },
    [setNodes, setEdges, handleDeleteNodeById, handleDuplicateNodeById, handleNodeDataChange, effectiveFlowVariables, addVariable, setManuallyPlacedNodes]
  );

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#fff]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700 text-sm font-semibold">Loading flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#fff]">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm mx-6 mt-4 rounded-2xl relative z-30">
        <div className="px-4 py-3">
          {/* First Row */}
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate("/flow-list")}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0 border border-gray-200"
              title="Back to flows"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                className="text-sm font-bold w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition-all bg-white"
                placeholder="Flow name *"
                autoFocus={!flowId}
              />
            </div>

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition-all bg-white"
                placeholder="Description (optional)"
              />
            </div>
          </div>

          {/* Second Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-xl border border-blue-200">
                  <MessageSquare size={14} className="text-blue-600" />
                  <span className="font-bold text-blue-900">{nodes.length}</span>
                  <span className="text-gray-600 font-medium">nodes</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-xl border border-emerald-200">
                  <GitBranch size={14} className="text-emerald-600" />
                  <span className="font-bold text-emerald-900">{edges.length}</span>
                  <span className="text-gray-600 font-medium">connections</span>
                </div>
                <FlowAiHelpMenu
                  open={showAiHelpPanel}
                  onOpenChange={(next) => {
                    setShowAiHelpPanel(next);
                    if (next) setShowVariablePanel(false);
                  }}
                  allowAiHelpDuringConversation={allowAiHelpDuringConversation}
                  onAllowAiHelpChange={handleAllowAiHelpChange}
                  aiHelpKnowledgeBase={aiHelpKnowledgeBase}
                  onKnowledgeBaseChange={handleAiHelpKnowledgeBaseChange}
                  error={aiHelpPanelError}
                />
                <button
                  onClick={() => {
                    setShowVariablePanel(!showVariablePanel);
                    if (showAiHelpPanel) setShowAiHelpPanel(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors ${
                    showVariablePanel
                      ? "bg-[#E7FCE8] border-[#008069]/30 text-[#008069]"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-[#E7FCE8] hover:border-[#008069]/30"
                  }`}
                >
                  <Variable size={14} />
                  <span className="font-bold">{effectiveFlowVariables.length}</span>
                  <span className="font-medium">vars</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Layout toggle controls */}
              <div className="flex items-center gap-1 mr-1">
                <button
                  onClick={() => setSnapToGrid((v) => !v)}
                  className={`p-2 rounded-lg border transition-colors ${
                    snapToGrid
                      ? "bg-blue-50 border-blue-300 text-blue-600"
                      : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
                  }`}
                  title={snapToGrid ? "Snap to grid ON" : "Snap to grid OFF"}
                >
                  <Grid3X3 size={14} />
                </button>
                <button
                  onClick={() => setLockManualPositions((v) => !v)}
                  className={`p-2 rounded-lg border transition-colors ${
                    lockManualPositions
                      ? "bg-amber-50 border-amber-300 text-amber-600"
                      : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
                  }`}
                  title={lockManualPositions ? "Lock manual positions ON – Format won't move dragged nodes" : "Lock manual positions OFF"}
                >
                  {lockManualPositions ? <Lock size={14} /> : <Unlock size={14} />}
                </button>
              </div>

              <FlowAddNodeMenu
                onSelectNode={addNode}
                onOpenAI={() => setShowAIPanel(true)}
              />

              <button
                type="button"
                onClick={() => setShowAIPanel((v) => !v)}
                className={`p-2.5 rounded-xl border transition-colors ${
                  showAIPanel
                    ? "bg-purple-100 border-purple-300 text-purple-700"
                    : "border-purple-200 text-purple-600 hover:bg-purple-50"
                }`}
                title="AI flow assistant"
              >
                <Sparkles size={16} />
              </button>

              <FlowToolsMenu
                onFormat={handleFormatFlow}
                onFormatAll={handleFormatAll}
                showFormatAll={manuallyPlacedNodes.size > 0}
                onClear={handleClearCanvas}
                onCopyJSON={handleCopyJSON}
                onImport={() => fileInputRef.current?.click()}
                onExport={handleExportJSON}
                jsonCopied={jsonCopied}
                nodesEmpty={nodes.length === 0}
                flowNameEmpty={!flowName.trim()}
              />

              <FlowTestSessionMenu
                visible={!!urlFlowId}
                testCustomerPhone={testCustomerPhone}
                onCustomerPhoneChange={(value) => {
                  setTestCustomerPhone(value);
                  setResetSessionMessage(null);
                }}
                resetSessionLoading={resetSessionLoading}
                resetSessionMessage={resetSessionMessage}
                onReset={handleResetTestSession}
              />

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={onFileInputChange}
              />

              <button
                onClick={() => navigate("/flow-list")}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors border border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => runValidation()}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors border ${
                  validationErrors.length > 0
                    ? "text-red-700 bg-red-50 border-red-300 hover:bg-red-100"
                    : "text-gray-700 hover:bg-gray-100 border-gray-300"
                }`}
                title="Validate flow"
              >
                <AlertTriangle size={16} />
                Validate
                {validationErrors.length > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {validationErrors.length}
                  </span>
                )}
              </button>
              <button
                onClick={handleSaveFlow}
                disabled={!flowName.trim() || saving}
                className="flex items-center gap-2 text-white px-5 py-2.5 text-sm rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                style={{ backgroundColor: "#4f46e5" }}
                title="Save flow to server"
              >
                {saving ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                {saving ? "Saving..." : "Save Flow"}
              </button>
              {lastSavedAt && (
                <span className="text-xs text-gray-400 self-center">
                  Saved {lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors / Warnings Panel */}
      {showValidationPanel && (validationErrors.length > 0 || validationWarnings.length > 0) && (
        <div className="mx-6 mt-2">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div
              className={`flex items-center justify-between px-4 py-2.5 border-b ${
                validationErrors.length > 0
                  ? "bg-red-50 border-red-100"
                  : "bg-amber-50 border-amber-100"
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle
                  size={16}
                  className={validationErrors.length > 0 ? "text-red-600" : "text-amber-600"}
                />
                <span
                  className={`text-sm font-bold ${
                    validationErrors.length > 0 ? "text-red-800" : "text-amber-800"
                  }`}
                >
                  {validationErrors.length > 0
                    ? `${validationErrors.length} error${validationErrors.length !== 1 ? "s" : ""}`
                    : ""}
                  {validationErrors.length > 0 && validationWarnings.length > 0 ? ", " : ""}
                  {validationWarnings.length > 0
                    ? `${validationWarnings.length} warning${validationWarnings.length !== 1 ? "s" : ""}`
                    : ""}
                </span>
                {validationErrors.length > 0 && (
                  <span className="text-xs text-red-600 font-medium">
                    — fix errors before exporting
                  </span>
                )}
                {validationErrors.length === 0 && validationWarnings.length > 0 && (
                  <span className="text-xs text-amber-600 font-medium">
                    — export is allowed, but review warnings
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowValidationPanel(false)}
                className={`p-1 rounded-lg transition-colors ${
                  validationErrors.length > 0
                    ? "hover:bg-red-100"
                    : "hover:bg-amber-100"
                }`}
              >
                <X
                  size={14}
                  className={validationErrors.length > 0 ? "text-red-500" : "text-amber-500"}
                />
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto px-4 py-2 space-y-1">
              {validationErrors.map((err, i) => (
                <div key={`err-${i}`} className="flex items-start gap-2 py-1">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-800 font-medium">{err.message}</span>
                </div>
              ))}
              {validationWarnings.map((warn, i) => (
                <div key={`warn-${i}`} className="flex items-start gap-2 py-1">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                  <span className="text-xs text-amber-700 font-medium">{warn.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden px-4 pb-4 mt-4 min-h-0">
        {/* AI Chat Panel */}
        <AIChatPanel
          visible={showAIPanel}
          onClose={() => setShowAIPanel(false)}
          nodes={nodes}
          edges={edges}
          onApplyFlow={handleApplyAIFlow}
          runValidation={runValidation}
          validationErrors={validationErrors}
          flowVariables={effectiveFlowVariables}
        />

        {/* Full-width Canvas */}
        <div
          ref={flowCanvasContainerRef}
          className="flex-1 relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden min-w-0"
        >
          {showVariablePanel && (
            <div className="absolute left-4 top-4 z-20 w-72 max-h-[calc(100%-2rem)] overflow-y-auto">
              <VariableManager
                flowVariables={effectiveFlowVariables}
                onAddVariable={addVariable}
                onRemoveVariable={removeVariable}
              />
            </div>
          )}

          <ReactFlow
            nodes={reactFlowNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgesDelete={handleEdgesDelete}
            onReconnectStart={onReconnectStart}
            onReconnect={onReconnect}
            onReconnectEnd={onReconnectEnd}
            onNodeClick={onNodeClick}
            onNodeDragStop={onNodeDragStop}
            onPaneMouseMove={onPaneMouseMove}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            fitView
            nodesConnectable={true}
            nodesDraggable={true}
            edgesReconnectable={true}
            elevateEdgesOnSelect={true}
            snapToGrid={snapToGrid}
            snapGrid={[20, 20]}
            connectionLineStyle={{ stroke: "#3b82f6", strokeWidth: 2 }}
            connectionLineType="smoothstep"
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: false,
              zIndex: 0,
              pathOptions: { borderRadius: 20, offset: 15 },
              style: { stroke: "#3b82f6", strokeWidth: 2 },
            }}
          >
            <Background variant={snapToGrid ? "dots" : "dots"} gap={20} />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        <NodeAddNodePicker
          open={nodeAddPicker.open}
          anchor={nodeAddPicker.open ? { x: nodeAddPicker.x, y: nodeAddPicker.y } : null}
          onSelect={(type) => addConnectedNode(nodeAddPicker.sourceNodeId, type)}
          onClose={() => setNodeAddPicker({ open: false, sourceNodeId: null, x: 0, y: 0 })}
        />
      </div>
    </div>
  );
};

export default FlowBuilder;
